import type { ReactNode } from 'react';
import { LoaderCircle, Minus, Plus } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { LIMITS, type UnitSystem } from '@dishly/shared';
import { updatePreferences } from '../lib/account';
import { cx } from '../lib/cx';
import { usePrefs } from '../stores/prefs';

export function Spinner({ className }: { className?: string }) {
  return <LoaderCircle className={cx('size-5 animate-spin', className)} aria-hidden />;
}

export function PageSpinner() {
  const { t } = useTranslation();
  return (
    <div className="flex min-h-[50dvh] items-center justify-center text-muted" role="status">
      <Spinner className="size-8" />
      <span className="sr-only">{t('common.loading')}</span>
    </div>
  );
}

export function EmptyState({
  icon,
  title,
  children,
  action,
}: {
  icon: ReactNode;
  title: string;
  children?: ReactNode;
  action?: ReactNode;
}) {
  return (
    <div className="flex flex-col items-center px-6 py-16 text-center">
      <div className="mb-4 flex size-16 items-center justify-center rounded-full bg-brand-soft text-brand">{icon}</div>
      <h2 className="font-display text-xl font-semibold">{title}</h2>
      {children && <div className="mt-2 max-w-md text-muted">{children}</div>}
      {action && <div className="mt-6">{action}</div>}
    </div>
  );
}

export function ServingsStepper({
  value,
  onChange,
  className,
}: {
  value: number;
  onChange: (servings: number) => void;
  className?: string;
}) {
  const { t } = useTranslation();
  return (
    <div className={cx('inline-flex items-center gap-1 rounded-full border border-line bg-surface p-1', className)}>
      <button
        type="button"
        className="icon-btn size-8"
        onClick={() => onChange(Math.max(1, value - 1))}
        disabled={value <= 1}
        aria-label={t('recipe.fewerServings')}
      >
        <Minus className="size-4" />
      </button>
      <span className="min-w-24 text-center text-sm font-semibold tabular-nums" aria-live="polite">
        {t('recipe.servings', { count: value })}
      </span>
      <button
        type="button"
        className="icon-btn size-8"
        onClick={() => onChange(Math.min(LIMITS.servings, value + 1))}
        disabled={value >= LIMITS.servings}
        aria-label={t('recipe.moreServings')}
      >
        <Plus className="size-4" />
      </button>
    </div>
  );
}

export function SegmentedControl<T extends string>({
  value,
  options,
  onChange,
  label,
}: {
  value: T;
  options: { value: T; label: ReactNode }[];
  onChange: (value: T) => void;
  label: string;
}) {
  return (
    <div role="radiogroup" aria-label={label} className="inline-flex rounded-full border border-line bg-surface p-1">
      {options.map((option) => (
        <button
          key={option.value}
          type="button"
          role="radio"
          aria-checked={value === option.value}
          onClick={() => onChange(option.value)}
          className={cx(
            'inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-sm font-semibold transition-colors',
            value === option.value ? 'bg-ink text-bg' : 'text-muted hover:text-ink',
          )}
        >
          {option.label}
        </button>
      ))}
    </div>
  );
}

/** Metric / imperial switch; also saves the choice to the account. */
export function UnitToggle() {
  const { t } = useTranslation();
  const units = usePrefs((s) => s.units);
  return (
    <SegmentedControl<UnitSystem>
      label={t('recipe.units')}
      value={units}
      onChange={(value) => void updatePreferences({ units: value })}
      options={[
        { value: 'metric', label: t('recipe.metric') },
        { value: 'imperial', label: t('recipe.imperial') },
      ]}
    />
  );
}

export function Badge({ children, tone = 'muted' }: { children: ReactNode; tone?: 'muted' | 'brand' }) {
  return (
    <span
      className={cx(
        'inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-semibold',
        tone === 'brand' ? 'bg-brand-soft text-brand' : 'bg-surface-2 text-muted',
      )}
    >
      {children}
    </span>
  );
}
